import React, { useState, useEffect, useRef, useCallback } from 'react';
import SanityImage from 'gatsby-plugin-sanity-image';
import {
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Flex,
  Text,
  Box,
  AspectRatio,
  Button,
  Container,
} from '@chakra-ui/react';
import { ArrowBackIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import { useStaticQuery, graphql } from 'gatsby';

const GridItem = ({ index, isFocused, onFocusChange, person, onOpen }) => {
  const handleFocus = () => {
    onFocusChange(index);
  };

  const handleItemClick = () => {
    onOpen(index);
  };

  return (
    <Box
      tabIndex={isFocused || index === 0 ? 0 : -1}
      onFocus={handleFocus}
      role='gridcell'
      as='button'
      padding={5}
      onClick={handleItemClick}
      ref={(el) => {
        if (isFocused && el) {
          el.focus();
        }
      }}
    >
      <Box as='figure' data-group>
        <AspectRatio
          ratio={1.6}
          width='100%'
          transition='transform 0.2s ease-in-out'
          overflow='hidden'
        >
          <Box
            transition='transform 1s ease-in-out'
            width='100%'
            height='100%'
            _groupHover={{
              transform: 'scale(1.1)',
            }}
          >
            <SanityImage
              {...person.image}
              alt={`Image of ${person.name}`}
              width={330}
              height={206}
              hotspot={person.image.hotspot}
              crop={person.image.crop}
              style={{ height: '100%', width: '100%', objectFit: 'cover' }}
            />
          </Box>
        </AspectRatio>
        <Box
          as='figcaption'
          lineHeight='1.2'
          mt={5}
          fontWeight='300'
          fontSize={{ base: 'xs', lg: 'md' }}
        >
          {person.name}
        </Box>
      </Box>
    </Box>
  );
};

const PeopleGrid = () => {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [instructionsDisplayed, setInstructionsDisplayed] = useState(false);
  const gridRef = useRef(null);
  const data = useStaticQuery(PEOPLE_QUERY);
  let { people } = data.data;
  const finalRef = useRef(null);

  people = people.sort((a, b) => {
    if (a.name < b.name) {
      return -1;
    }
    return 1;
  });

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalPrev = () => {
    setFocusedIndex((prevIndex) => Math.max(0, prevIndex - 1));
  };

  const handleModalNext = useCallback(() => {
    setFocusedIndex((prevIndex) => Math.min(people.length - 1, prevIndex + 1));
  }, [people.length]);

  useEffect(() => {
    finalRef.current = gridRef.current.children[focusedIndex];
  }, [focusedIndex]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        // Set finalRef to the previous element in the grid
        handleModalPrev();
      } else if (event.key === 'ArrowRight') {
        // Set finalRef to the next element in the grid
        handleModalNext();
      } else if (event.key === 'Tab' && !event.shiftKey) {
        setInstructionsDisplayed(true);
        // If tab without shift, move focus out of the grid
        gridRef.current && gridRef.current.blur();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, handleModalNext, people.length]);

  // Set the person to the one at the current modal index
  const person = people[focusedIndex];

  const AlternatesText = ({ alternates }) => {
    const alternatesArray = alternates.split(',');
    if (alternatesArray.length === 1) {
      return 'Alternate';
    }
    return 'Alternates';
  };

  return (
    <Container maxWidth='container.xl' px={{ base: 20, xl: 60 }}>
      {instructionsDisplayed && (
        <Box textAlign='center' padding={10} mb={20} background='grey.light'>
          <Text fontWeight='400'>
            Use the left and right arrow keys on your keyboard to navigate
          </Text>
        </Box>
      )}
      <Grid
        gridTemplateColumns={['repeat(2, 1fr)', 'repeat(3, 1fr)', 'repeat(4, 1fr)']}
        alignItems='flex-start'
        gap={20}
        role='grid'
        ref={gridRef}
        tabIndex={-1}
      >
        {people.map((person, index) => (
          <GridItem
            key={index}
            index={index}
            isFocused={index === focusedIndex}
            onFocusChange={setFocusedIndex}
            onOpen={handleModalOpen}
            person={person}
          />
        ))}
      </Grid>

      <Modal isOpen={isModalOpen} onClose={handleModalClose} size='full' finalFocusRef={finalRef}>
        <ModalOverlay />
        <ModalContent
          background='rgba(255, 255, 255, 0.5)'
          backdropFilter='auto'
          backdropBlur='6px'
        >
          <ModalCloseButton background='white' padding={20} borderRadius='100%' />
          <ModalBody padding={30} width='100%' maxWidth='600px' margin='auto'>
            <Box background='white' padding={30}>
              {person && (
                <>
                  <AspectRatio maxW='480px' ratio={1.6} background='grey.light' mb={10}>
                    <SanityImage
                      {...person.image}
                      alt={`Image of ${person.name}`}
                      width={480}
                      height={300}
                      hotspot={person.image.hotspot}
                      crop={person.image.crop}
                      style={{ height: '100%', width: '100%', objectFit: 'cover' }}
                    />
                  </AspectRatio>
                  <Flex flexDirection='column' gap={10}>
                    <Text as='h3' textStyle='h3'>
                      {person.name}
                    </Text>
                    <Text as='h4' textStyle='h4'>
                      <AlternatesText alternates={person.alternates} />
                    </Text>
                    <Text>{person.alternates}</Text>
                    <Text as='h4' textStyle='h4'>
                      Constituency
                    </Text>
                    <Text>{person.constituency}</Text>
                  </Flex>
                </>
              )}
            </Box>
          </ModalBody>
          <ModalFooter padding='0'>
            <Flex justifyContent='space-between' width='100%' background='white' padding={20}>
              <Button
                aria-label='Previous'
                onClick={handleModalPrev}
                isDisabled={focusedIndex === 0}
              >
                <ArrowBackIcon />
              </Button>
              <Button
                aria-label='Next'
                onClick={handleModalNext}
                isDisabled={focusedIndex === people.length - 1}
              >
                <ArrowForwardIcon />
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

const PEOPLE_QUERY = graphql`
  query PEOPLEQUERY {
    data: allSanityPerson {
      people: nodes {
        alternates
        constituency
        image: _rawImage
        key: _key
        name
      }
    }
  }
`;

export default PeopleGrid;
